import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import PlayCircleOutlineOutlined from '@mui/icons-material/PlayCircleOutlineOutlined';
import PortalLayout from '../../components/layouts/PortalLayout';
import { theme, VimeoPlayer } from '@omnigenbiodata/ui';
import { useTranslation } from 'react-i18next';

const primaryTint = theme.palette.grey[100];
const primaryTintStrong = theme.palette.info.lighter;

function ParticipantVideoScene() {
  const { t } = useTranslation('portal');
  return (
    <PortalLayout>
      <Box pb={3}>
        <Paper
          elevation={4}
          sx={{
            mb: 4,
            border: 1,
            borderColor: 'rgba(16, 53, 68, 0.18)',
            bgcolor: primaryTint,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              height: 12,
              bgcolor: 'primary.main',
            }}
          />

          <Box p={{ xs: 3, md: 5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 2,
              }}
            >
              <Box
                sx={{
                  width: 84,
                  height: 84,
                  borderRadius: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'background.paper',
                  flexShrink: 0,
                }}
              >
                <PlayCircleOutlineOutlined color="primary" sx={{ fontSize: 56 }} />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Chip
                  label="Participant Enrolment Video"
                  color="primary"
                  variant="outlined"
                  sx={{
                    mb: 1,
                    fontWeight: 700,
                  }}
                />

                <Typography
                  variant="h3"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    color: 'primary.main',
                    lineHeight: 1.1,
                  }}
                >
                  {t('vid.title')}
                </Typography>
              </Box>
            </Box>

            <Typography
              variant="h6"
              component="p"
              color="text.secondary"
              sx={{
                maxWidth: 920,
                lineHeight: 1.35,
              }}
            >
              {t('vid.text')}
            </Typography>
          </Box>
        </Paper>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper
              elevation={4}
              sx={{
                border: 1,
                borderColor: 'rgba(16, 53, 68, 0.18)',
                bgcolor: 'background.paper',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: 8,
                  bgcolor: 'primary.main',
                }}
              />

              <Box p={{ xs: 2, md: 3 }}>
                <VimeoPlayer videoUrl={t('vid.videoUrl')} title={t('vid.videoTitle')} />
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={4}
              sx={{
                position: { md: 'sticky' },
                top: { md: 24 },
                border: 1,
                borderColor: 'rgba(16, 53, 68, 0.18)',
                bgcolor: primaryTintStrong,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: 8,
                  bgcolor: 'primary.main',
                }}
              />

              <Box p={3}>
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    color: 'primary.main',
                    mb: 1,
                  }}
                >
                  Before You Continue
                </Typography>

                <Typography variant="body1" component="p" color="text.secondary" sx={{ lineHeight: 1.45, mb: 3 }}>
                  The participant should have the opportunity to watch the video and ask questions before proceeding
                  with the enrolment or sample collection workflow.
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                  <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                    Watch the full video
                  </Typography>
                  <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                    Confirm the participant understands the key information
                  </Typography>
                  <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                    Allow time for questions
                  </Typography>
                  <Typography component="li" variant="body1" color="text.secondary">
                    Continue only when the participant is ready
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </PortalLayout>
  );
}

export default ParticipantVideoScene;
